import { ArrowRight, Check, Phone } from "lucide-react";
import { BeforeAfterSlider } from "@/components/site/BeforeAfter";
import { Breadcrumbs, PageHero } from "@/components/site/PageHero";
import { QuoteForm } from "@/components/site/QuoteForm";
import { Button } from "@/components/ui/button";
import {
  acceptedProjectImages,
  type BeforeAfterProject,
  type ProjectImage,
} from "@/config/projects";
import { business } from "@/config/business";
import { findServiceArea } from "@/config/service-areas";
import { serviceBySlug } from "@/config/services";
import { trackAnalyticsEvent } from "@/lib/analytics";
import type { PageUi } from "@/i18n/content";
import { galleryCopyEn } from "@/i18n/gallery-copy";
import { useI18n } from "@/i18n";
import { localizeServiceArea, localizeServicePage } from "@/i18n/content";
import { getMarketingCopy } from "@/i18n/marketing-copy";

type PathBuilder = (href: string) => string;

/** Detail page for a single before/after portfolio gallery. */
export function ProjectDetailPage({
  project,
  path,
  ui,
}: {
  project: BeforeAfterProject;
  path: PathBuilder;
  ui?: PageUi | undefined;
}) {
  const { locale } = useI18n();
  const marketing = getMarketingCopy(locale);
  const copy = marketing?.portfolio;
  const baseService = project.service ? serviceBySlug[project.service] : undefined;
  const service = baseService ? localizeServicePage(baseService, locale) : undefined;
  const relatedServicePages = project.relatedServices
    .map((slug) => serviceBySlug[slug])
    .filter((item): item is NonNullable<typeof item> => Boolean(item))
    .map((item) => localizeServicePage(item, locale));
  const baseRelatedArea = project.relatedLocation ? findServiceArea(project.relatedLocation) : null;
  const relatedArea = baseRelatedArea ? localizeServiceArea(baseRelatedArea, locale) : null;

  const materials = project.materials ?? [];
  const beforeImages = acceptedProjectImages(project.beforeImages);
  const afterImages = acceptedProjectImages(project.afterImages);
  const canShowSlider = beforeImages.length > 0 && afterImages.length > 0;
  const cityName = relatedArea?.name ?? project.city;
  const heroBody = ui?.corePages.gallery.projectHeroBody ?? galleryCopyEn.projectHeroBody;

  return (
    <>
      <PageHero
        eyebrow={ui?.corePages.gallery.eyebrow ?? galleryCopyEn.eyebrow}
        title={project.title}
        body={heroBody}
        image={{
          ...project.featuredImage,
          width: project.width,
          height: project.height,
        }}
        rotatingVision={false}
        breadcrumbs={
          <Breadcrumbs
            items={[
              { label: ui?.common.home ?? "Home", href: path("/") },
              { label: copy?.beforeAfter ?? "Before & After", href: path("/before-after") },
              { label: project.title },
            ]}
          />
        }
      >
        <div className="flex flex-wrap gap-2">
          {cityName ? (
            <span className="rounded bg-primary-foreground/10 px-3 py-1.5 text-[0.7rem] tracking-wide uppercase text-primary-foreground/85">
              {cityName}
            </span>
          ) : project.location ? (
            <span className="rounded bg-primary-foreground/10 px-3 py-1.5 text-[0.7rem] tracking-wide uppercase text-primary-foreground/85">
              {project.location}
            </span>
          ) : null}
          {project.roofType ? (
            <span className="rounded bg-primary-foreground/10 px-3 py-1.5 text-[0.7rem] tracking-wide uppercase text-primary-foreground/85">
              {project.roofType}
            </span>
          ) : null}
          {service ? (
            <span className="rounded bg-primary-foreground/10 px-3 py-1.5 text-[0.7rem] tracking-wide uppercase text-primary-foreground/85">
              {service.shortTitle}
            </span>
          ) : null}
        </div>
      </PageHero>

      {canShowSlider ? (
        <section className="section-y">
          <div className="container-page">
            <p className="eyebrow">{copy?.primaryComparison ?? "Primary comparison"}</p>
            <h2 className="display-2 mt-3 max-w-3xl text-balance">
              {copy?.beforeAfter ?? "Before and after"}
            </h2>
            <div className="mt-8 max-w-5xl">
              <BeforeAfterSlider project={project} className="rounded-xl" />
            </div>
            {(project.featuredImage.caption || project.shortProblem) && (
              <p className="mt-4 max-w-3xl text-sm text-muted-foreground">
                {project.featuredImage.caption ?? project.shortProblem}
              </p>
            )}
          </div>
        </section>
      ) : null}

      <section className="section-y border-y border-border bg-muted/30">
        <div className="container-page grid gap-10 lg:grid-cols-3">
          <div>
            <p className="eyebrow">{copy?.problem ?? "Problem"}</p>
            <h2 className="mt-3 text-xl font-semibold tracking-tight">
              {copy?.problemTitle ?? "What needed attention"}
            </h2>
            <p className="mt-4 text-sm leading-7 text-muted-foreground">
              {project.problem ??
                project.shortProblem ??
                copy?.problemFallback ??
                "No problem narrative supplied."}
            </p>
          </div>
          <div>
            <p className="eyebrow">{copy?.diagnosis ?? "Diagnosis"}</p>
            <h2 className="mt-3 text-xl font-semibold tracking-tight">
              {copy?.diagnosisTitle ?? "Assessment focus"}
            </h2>
            <p className="mt-4 text-sm leading-7 text-muted-foreground">
              {project.diagnosis ??
                copy?.diagnosisFallback ??
                "Assessment focus is documented during the roof review."}
            </p>
          </div>
          <div>
            <p className="eyebrow">{copy?.solution ?? "Solution path"}</p>
            <h2 className="mt-3 text-xl font-semibold tracking-tight">
              {copy?.solutionTitle ?? "How work is framed"}
            </h2>
            <p className="mt-4 text-sm leading-7 text-muted-foreground">
              {project.solution ??
                copy?.solutionFallback ??
                "Solution narrative is added with the project scope."}
            </p>
          </div>
        </div>
      </section>

      <section className="section-y">
        <div className="container-page grid gap-12 lg:grid-cols-[1fr_0.85fr] lg:gap-16">
          <div>
            <p className="eyebrow">{copy?.materials ?? "Materials (categories)"}</p>
            <h2 className="display-2 mt-3 text-balance">
              {copy?.materialsTitle ?? "Material language for planning"}
            </h2>
            <p className="mt-4 max-w-xl text-sm leading-7 text-muted-foreground">
              {copy?.materialsBody ??
                "Material categories used when planning this type of roofing work."}
            </p>
            {materials.length ? (
              <ul className="mt-8 space-y-3">
                {materials.map((item) => (
                  <li
                    key={item}
                    className="flex gap-3 text-sm leading-relaxed text-muted-foreground"
                  >
                    <Check className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-8 text-sm text-muted-foreground">
                {copy?.noMaterials ?? "No materials listed."}
              </p>
            )}

            {project.completionDate ? (
              <dl className="mt-10 grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-border bg-card p-4">
                  <dt className="text-xs tracking-[0.12em] uppercase text-muted-foreground">
                    {copy?.completionDate ?? "Completion date"}
                  </dt>
                  <dd className="mt-1 text-sm font-medium">{project.completionDate}</dd>
                </div>
              </dl>
            ) : null}
          </div>

          <aside className="rounded-xl border border-border bg-primary p-7 text-primary-foreground md:p-9">
            <p className="eyebrow text-accent">{copy?.nextStep ?? "Next step"}</p>
            <h2 className="mt-3 text-2xl font-semibold tracking-tight">
              {copy?.nextStepTitle ?? "Discuss your roof with an assessment"}
            </h2>
            <p className="mt-4 text-sm leading-7 text-primary-foreground/75">
              {copy?.nextStepBody ??
                "Real scopes start with the building in front of us. Request a roof assessment for a property-specific recommendation."}
            </p>
            <div className="mt-8 flex flex-col gap-3">
              <Button variant="accent" size="xl" asChild>
                <a
                  href="#assessment"
                  onClick={() =>
                    trackAnalyticsEvent({
                      event: "service_cta_click",
                      cta_location: "project_detail",
                      service: project.service ?? "portfolio",
                    })
                  }
                >
                  {ui?.common.requestAssessment ?? "Request a Roof Assessment"}
                </a>
              </Button>
              <Button variant="outlineDark" size="xl" asChild>
                <a
                  href={business.phoneHref}
                  className="gap-2"
                  onClick={() =>
                    trackAnalyticsEvent({
                      event: "phone_click",
                      link_location: `project_detail_${project.slug}`,
                    })
                  }
                >
                  <Phone aria-hidden="true" /> {ui?.common.call ?? "Call"} {business.phone}
                </a>
              </Button>
            </div>
          </aside>
        </div>
      </section>

      <ImageGallery
        title={copy?.beforeImages ?? "Before images"}
        eyebrow={`${copy?.beforeImages ?? "Before"} (${beforeImages.length})`}
        images={beforeImages}
        width={project.width}
        height={project.height}
        noImages={copy?.noImages}
      />
      <ImageGallery
        title={copy?.afterImages ?? "After images"}
        eyebrow={`${copy?.afterImages ?? "After"} (${afterImages.length})`}
        images={afterImages}
        width={project.width}
        height={project.height}
        noImages={copy?.noImages}
        muted
      />

      <section className="section-y border-t border-border">
        <div className="container-page">
          <p className="eyebrow">{copy?.related ?? "Related"}</p>
          <h2 className="display-2 mt-3 text-balance">
            {copy?.relatedTitle ?? "Services and service areas"}
          </h2>
          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <div>
              <h3 className="text-sm font-semibold tracking-[0.12em] uppercase text-muted-foreground">
                {copy?.relatedServices ?? "Related services"}
              </h3>
              <ul className="mt-4 space-y-3">
                {service ? (
                  <li>
                    <a
                      href={path(service.path || `/${service.slug}`)}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
                    >
                      {copy?.primary ?? "Primary"}: {service.title}
                      <ArrowRight className="size-3.5" aria-hidden="true" />
                    </a>
                  </li>
                ) : null}
                {relatedServicePages.map((item) =>
                  item && item.slug !== service?.slug ? (
                    <li key={item.slug}>
                      <a
                        href={path(item.path || `/${item.slug}`)}
                        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                      >
                        {item.title}
                        <ArrowRight className="size-3.5" aria-hidden="true" />
                      </a>
                    </li>
                  ) : null,
                )}
              </ul>
            </div>
            <div>
              <h3 className="text-sm font-semibold tracking-[0.12em] uppercase text-muted-foreground">
                {copy?.serviceArea ?? "Service area"}
              </h3>
              {relatedArea?.confirmed ? (
                <p className="mt-4 text-sm leading-7 text-muted-foreground">
                  {copy?.relatedArea ?? "Related service area"}:{" "}
                  <a
                    href={path(`/service-areas/${relatedArea.slug}`)}
                    className="font-medium text-foreground underline-offset-4 hover:underline"
                  >
                    {relatedArea.name}
                  </a>
                </p>
              ) : (
                <p className="mt-4 text-sm leading-7 text-muted-foreground">
                  {project.location}. Browse confirmed service coverage for project planning.
                </p>
              )}
              <a
                href={path("/service-areas")}
                className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
              >
                {copy?.browseAreas ?? "View service areas"}
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </a>
              <a
                href={path("/before-after")}
                className="mt-3 flex items-center gap-1.5 text-sm font-medium text-foreground underline-offset-4 hover:underline"
              >
                {copy?.backToGallery ?? "Back to portfolio gallery"}
                <ArrowRight className="size-3.5" aria-hidden="true" />
              </a>
            </div>
          </div>
        </div>
      </section>

      <section id="assessment" className="section-y bg-muted/50">
        <div className="container-page grid gap-12 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
          <div>
            <p className="eyebrow">{copy?.assessmentRequest ?? "Assessment request"}</p>
            <h2 className="display-2 mt-3 text-balance">
              {copy?.assessmentTitle ?? "Tell us what you are seeing"}
            </h2>
            <p className="mt-4 text-sm leading-7 text-muted-foreground">
              {copy?.assessmentBody ??
                "Share the property location, roof type if known and the concern you want reviewed. An on-site assessment determines the right next step for your building."}
            </p>
          </div>
          <QuoteForm />
        </div>
      </section>
    </>
  );
}

function ImageGallery({
  title,
  eyebrow,
  images,
  width,
  height,
  muted = false,
  noImages,
}: {
  title: string;
  eyebrow: string;
  images: readonly ProjectImage[];
  width: number;
  height: number;
  muted?: boolean;
  noImages?: string | undefined;
}) {
  if (images.length === 0) {
    return (
      <section className={muted ? "section-y bg-muted/25" : "section-y pt-0"}>
        <div className="container-page">
          <p className="eyebrow">{eyebrow}</p>
          <h2 className="display-2 mt-3 text-balance">{title}</h2>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground">
            {noImages ?? "No images are available in this slot yet."}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className={muted ? "section-y bg-muted/25" : "section-y pt-0"}>
      <div className="container-page">
        <p className="eyebrow">{eyebrow}</p>
        <h2 className="display-2 mt-3 text-balance">{title}</h2>
        <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {images.map((image, index) => (
            <li key={`${image.src}-${index}`} className="min-w-0">
              <figure className="overflow-hidden rounded-lg border border-border bg-card">
                <div className="aspect-[3/2] bg-muted">
                  <img
                    src={image.src}
                    alt={image.alt}
                    width={width}
                    height={height}
                    loading="lazy"
                    decoding="async"
                    className="size-full object-cover"
                  />
                </div>
                {(image.caption || image.alt) && (
                  <figcaption className="p-3 text-xs leading-relaxed text-muted-foreground">
                    {image.caption ?? image.alt}
                  </figcaption>
                )}
              </figure>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
